'use client';

import { faMessageLines, faPen, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip } from '@pickleballinc/react-ui';
import dayjs from 'dayjs';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState } from 'react';

import { SmallActionButton } from '@/components/atoms';
import { EditTeamSettingsForm, InviteOrResendCodeModal, SendTextForm } from '@/components/forms';
import { GroupPlayers } from '@/components/forms/modals/SendTextForm';
import { TeamPlayersTable } from '@/components/molecules';
import { EditTeamSettingsData, SendTextData } from '@/entities';
import { Result } from '@/entities';
import { currencyFormats } from '@/entities/CurrencyFormat';
import { PermissionRole } from '@/entities/PermissionRoles';
import { ApproveTransfers, TeamLeagueInfo } from '@/entities/TeamLeagueInfo';
import { ActivityPlayerInfoData, TeamPlayerInfo } from '@/entities/TeamPlayerInfo';
import {
	useCreateInvitationCodeMutate,
	useInvitationCodeQuery,
	useSendMessageMutate,
	useUpdateInvitationCodeMutate,
	useUpdateIsFreeAgentMutate
} from '@/hooks/hooks';
import { CurrencyDto } from '@/infrastructure/network/dto/CurrencyDto';
import { useOrganizationStore } from '@/store/organization-store';
import { usePermissionStore } from '@/store/permission-store';
import { useTeamLeagueStore } from '@/store/team-league-store';

interface TeamInfoDataProps {
	teamLeagueId: string;
	teamId: string;
	name: string;
	players: TeamPlayerInfo[] | ActivityPlayerInfoData[];
	seasonId: string;
	isFreeAgents?: boolean;
	leagueInfo: TeamLeagueInfo | undefined;
	currencies: Result<CurrencyDto[]> | undefined;
}

interface SendMessagePayload {
	users_uuid: string[];
	message: string;
}

function isDateBeforeToday(dateString: string | undefined | null): boolean {
	return dateString ? dayjs(dateString).isBefore(dayjs(), 'day') : false;
}

function isDateDisabled(dateDisabled: string | undefined | null): boolean {
	if (!dateDisabled) {
		return false;
	}
	return dateDisabled !== '0001-01-01T00:00:00Z';
}

export const TeamInfoData = ({ name, players, teamLeagueId, teamId, seasonId, isFreeAgents = false, leagueInfo, currencies }: TeamInfoDataProps) => {
	const path = usePathname();
	const isActiveTeams = path.includes('active-teams');

	const store = useOrganizationStore();
	const permissionStore = usePermissionStore();
	const teamLeagueStore = useTeamLeagueStore();
	const shouldHidePlayerMoveButton =
		teamLeagueStore.playerMoves === ApproveTransfers.TRANSFER_APPROVAL_ONLY_COMMISSIONER_CAN_INITIATE &&
		permissionStore.userPermission !== PermissionRole.SUPER_ADMIN &&
		permissionStore.userPermission !== PermissionRole.TEAM_LEAGUES_GLOBAL_MANAGER &&
		permissionStore.userPermission !== PermissionRole.COMMISSIONER &&
		permissionStore.userPermission !== PermissionRole.PRIMARY_MANAGER;
	const [playersList, setPlayersList] = useState<GroupPlayers[]>([]);
	const [isInvitationCodeModalOpen, setIsInvitationCodeModalOpen] = useState(false);
	const [isInviteOrResendCodeModalOpen, setIsInviteOrResendCodeModalOpen] = useState(false);
	const [isSendMessageModalOpen, setIsSendMessageModalOpen] = useState(false);

	const [playerId, setPlayerId] = useState('');
	const [sendMessagModalTitle, setSendMessagModalTitle] = useState('');
	const [modalTitle, setModalTitle] = useState('');

	const { data: invitationCode } = useInvitationCodeQuery(store.teamLeagueUuid, store.organizationUuid, teamId);
	const currency = currencies?.data?.find((currency: CurrencyDto) => currency.id === leagueInfo?.currencyId.toString());

	const { mutateAsync: updateIsFreeAgent } = useUpdateIsFreeAgentMutate();
	const { mutateAsync: updateInvitationCode } = useUpdateInvitationCodeMutate();
	const { mutateAsync: createInvitationCode } = useCreateInvitationCodeMutate();
	const { mutateAsync: sendMessage } = useSendMessageMutate();
	const ratingOption = leagueInfo?.ratingOption;

	const handleModalOpen = (title: string) => {
		setModalTitle(title);
		setIsInviteOrResendCodeModalOpen(true);
	};

	const sendMessageSubmit = async (data: SendTextData) => {
		const requestPayload: SendMessagePayload = {
			users_uuid: playerId ? [playerId] : Object.keys(data.players).filter((id) => data.players[id]),
			message: data.message
		};
		if (requestPayload.users_uuid.length === 0) return;

		await sendMessage({ teamLeagueId: store.teamLeagueUuid, organizationId: store.organizationUuid, requestPayload });
		setIsSendMessageModalOpen(false);
	};

	const handleSendMessageModalOpen = (title: string, groupOfPlayers: boolean, playerId?: string) => {
		setSendMessagModalTitle(title);

		setPlayersList(
			groupOfPlayers
				? players.map((player, index) => ({
						uuid: `${player.uuid}-${index}`,
						id: player.uuid,
						name: player.fullname,
						isDisabled: !player.sendTextAlerts
					}))
				: []
		);

		setPlayerId(playerId || '');
		setIsSendMessageModalOpen(true);
	};

	const handleInvitationCodeSubmit = async (data: EditTeamSettingsData) => {
		if (data.isFreeAgent !== (isFreeAgents ?? false)) {
			const requestPayload = {
				allow_free_agent_requests: data.isFreeAgent
			};

			await updateIsFreeAgent({ teamLeagueId: store.teamLeagueUuid, organizationId: store.organizationUuid, teamId, requestPayload });
		}

		if (data.invitationCode && (!invitationCode?.data?.code || isDateBeforeToday(invitationCode?.data?.dateOfExpiry))) {
			const requestPayload = {
				code: data.invitationCode,
				date_of_expiry: data.expirationDate,
				number_of_uses: data.numberOfTimesCodeCanBeUsed ? data.numberOfTimesCodeCanBeUsed : null
			};

			await createInvitationCode({ teamLeagueId: store.teamLeagueUuid, organizationId: store.organizationUuid, teamId, requestPayload });

			setIsInvitationCodeModalOpen(false);
			return;
		}

		if (
			data.invitationCode &&
			invitationCode?.data?.uuid &&
			(!isDateDisabled(invitationCode?.data?.dateDisabled) !== data.isActive ||
				invitationCode?.data?.dateOfExpiry !== data.expirationDate ||
				invitationCode?.data?.numberOfUses !== (data.numberOfTimesCodeCanBeUsed == '' ? null : data.numberOfTimesCodeCanBeUsed) ||
				invitationCode?.data?.code !== data.invitationCode)
		) {
			const dateDisabled = !data.isActive ? dayjs().toISOString() : null;

			const requestPayload = {
				uuid: invitationCode?.data?.uuid,
				code: data.invitationCode,
				date_of_expiry: data.expirationDate,
				number_of_uses: data.numberOfTimesCodeCanBeUsed ? data.numberOfTimesCodeCanBeUsed : null,
				date_disabled: dateDisabled
			};

			await updateInvitationCode({
				teamLeagueId: store.teamLeagueUuid,
				organizationId: store.organizationUuid,
				teamId,
				codeId: invitationCode?.data?.uuid,
				requestPayload
			});
		}

		setIsInvitationCodeModalOpen(false);
	};

	return (
		<div className="flex flex-col gap-2">
			<div className="flex items-center gap-4">
				<span className="text-lg font-bold lg:text-xl">{name}</span>
				<FontAwesomeIcon onClick={() => setIsInvitationCodeModalOpen(true)} className="cursor-pointer" icon={faPen} />
			</div>

			<div className="flex flex-col gap-4 sm:flex-row">
				{!shouldHidePlayerMoveButton && (
					<Link
						className="flex h-[26px] items-center justify-center gap-1 rounded border-darkorange-100 bg-darkorange-50 px-2 py-1 text-xs font-semibold leading-[18px] text-darkorange-400 "
						href={`/team-leagues/${teamLeagueId}/teams/${teamId}/player-moves`}
						prefetch={false}
					>
						<FontAwesomeIcon icon={faPlus} />
						Add or move player
					</Link>
				)}

				{isActiveTeams ? (
					<SmallActionButton prefixIcon={faMessageLines} onClick={() => handleSendMessageModalOpen(name, true)}>
						Send text
					</SmallActionButton>
				) : null}

				<div className="flex gap-4">
					{!invitationCode?.data?.code ? (
						<Tooltip placement="bottom">
							<Tooltip.Trigger asChild>
								<SmallActionButton>Invite with code</SmallActionButton>
							</Tooltip.Trigger>
							<Tooltip.Content>
								<p>This team doesn`t have invite code.</p>
								<p>Please set it up to be able to invite people.</p>
							</Tooltip.Content>
						</Tooltip>
					) : (
						<SmallActionButton onClick={() => handleModalOpen('Invite with code')}>Invite with code</SmallActionButton>
					)}

					{/* <SmallActionButton suffixIcon={faQrcode}>Invite with QR code</SmallActionButton> */}
				</div>
			</div>

			<div>
				<div>
					<TeamPlayersTable
						teamData={{ name, players, teamId }}
						handleModalOpen={handleModalOpen}
						handleSendMessageModalOpen={handleSendMessageModalOpen}
						teamLeagueId={teamLeagueId}
						seasonId={seasonId}
						ratings={!ratingOption || ratingOption === 'RATING_DUPR' ? 'DUPR' : 'WPR'}
						currency={leagueInfo && currency?.abbreviation ? currencyFormats[currency.abbreviation] : null}
					/>
				</div>
			</div>
			{isInvitationCodeModalOpen ? (
				<EditTeamSettingsForm
					title="Edit codes & requests"
					onClose={() => setIsInvitationCodeModalOpen(false)}
					onSubmit={handleInvitationCodeSubmit}
					currentCode={invitationCode?.data?.code}
					allowFreeAgentRequests={isFreeAgents}
					numberOfUses={invitationCode?.data?.numberOfUses}
					dateOfExpiry={invitationCode?.data?.dateOfExpiry}
					dateDisabled={invitationCode?.data?.dateDisabled}
					isActiveCode={!isDateDisabled(invitationCode?.data?.dateDisabled)}
					codeUuid={invitationCode?.data?.uuid}
					teamUuid={invitationCode?.data?.teamUuid}
					teamName={name}
				/>
			) : null}
			{isInviteOrResendCodeModalOpen ? (
				<InviteOrResendCodeModal
					title={modalTitle}
					invitationCode={invitationCode?.data?.code}
					onClose={() => setIsInviteOrResendCodeModalOpen(false)}
				/>
			) : null}

			{isSendMessageModalOpen ? (
				<SendTextForm
					title={`Send message to ${sendMessagModalTitle}`}
					players={playersList}
					onClose={() => setIsSendMessageModalOpen(false)}
					onSubmit={sendMessageSubmit}
				/>
			) : null}
		</div>
	);
};
